class SwivotHome {
  constructor(root) {
    if (root.dataset.swivotInitialized === 'true') return;

    root.dataset.swivotInitialized = 'true';
    this.root = root;
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.details = [...root.querySelectorAll('.swivot-fundamentals details')];
    this.bindAccordions();
    this.setupDemo();
    this.observeSections();
    this.setupStickyBuy();
    this.startHeroMotion();
  }

  startHeroMotion() {
    if (this.motionQuery.matches) return;

    this.root.classList.add('swivot-home--motion');
  }

  setupStickyBuy() {
    const stickyBuy = this.root.querySelector('.swivot-sticky-buy');
    const hero = this.root.querySelector('.swivot-hero');
    const purchase = this.root.querySelector('.swivot-purchase');
    if (!stickyBuy || !hero) return;

    if (!('IntersectionObserver' in window)) {
      stickyBuy.dataset.visible = 'true';
      stickyBuy.removeAttribute('inert');
      stickyBuy.setAttribute('aria-hidden', 'false');
      this.root.dataset.stickyBuyVisible = 'true';
      return;
    }

    let heroIsVisible = true;
    let purchaseIsVisible = false;

    const updateStickyBuy = () => {
      const isVisible = !heroIsVisible && !purchaseIsVisible;
      stickyBuy.dataset.visible = String(isVisible);
      stickyBuy.toggleAttribute('inert', !isVisible);
      stickyBuy.setAttribute('aria-hidden', String(!isVisible));
      this.root.dataset.stickyBuyVisible = String(isVisible);
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target === hero) heroIsVisible = entry.isIntersecting;
        if (entry.target === purchase) purchaseIsVisible = entry.isIntersecting;
      });
      updateStickyBuy();
    }, { threshold: 0.05 });
    observer.observe(hero);
    if (purchase) observer.observe(purchase);
  }

  bindAccordions() {
    this.details.forEach((detail) => {
      const summary = detail.querySelector('summary');

      if (!summary) return;

      summary.addEventListener('click', () => {
        if (detail.open) return;

        this.details.forEach((other) => {
          if (other !== detail) other.removeAttribute('open');
        });
      });
    });
  }

  setupDemo() {
    const demo = this.root.querySelector('[data-swivot-demo]');
    const video = demo?.querySelector('[data-swivot-demo-video]');
    const status = demo?.querySelector('[data-swivot-demo-status]');
    const cue = demo?.querySelector('[data-swivot-demo-cue]');
    const setup = demo?.querySelector('[data-swivot-demo-setup]');
    const move = demo?.querySelector('[data-swivot-demo-move]');
    const feel = demo?.querySelector('[data-swivot-demo-feel]');
    const correct = demo?.querySelector('[data-swivot-demo-correct]');
    const recovery = demo?.querySelector('[data-swivot-demo-recovery]');
    const captions = demo?.querySelector('[data-swivot-demo-captions]');
    const panel = demo?.querySelector('[role="tabpanel"]');
    const chapters = [...(demo?.querySelectorAll('.swivot-demo__chapter') || [])];

    if (!demo || !video || chapters.length === 0) return;

    let activeIndex = 0;

    const formatDuration = (seconds) => {
      if (!Number.isFinite(seconds)) return '';
      const minutes = Math.floor(seconds / 60);
      const remaining = Math.round(seconds % 60).toString().padStart(2, '0');
      return `${minutes}:${remaining}`;
    };

    const updateStatus = (message) => {
      if (status) status.textContent = message;
    };

    const selectChapter = (chapter, index) => {
      const src = chapter.dataset.videoSrc;
      const poster = chapter.dataset.posterSrc;
      const captionSrc = chapter.dataset.captionSrc;
      const label = chapter.dataset.chapterLabel;
      const chapterCue = chapter.dataset.chapterCue;
      const chapterSetup = chapter.dataset.chapterSetup;
      const chapterMove = chapter.dataset.chapterMove;
      const chapterFeel = chapter.dataset.chapterFeel;
      const chapterCorrect = chapter.dataset.chapterCorrect;

      if (!src || !poster || !label) return;

      activeIndex = index;
      video.pause();
      chapters.forEach((item, itemIndex) => {
        const isActive = itemIndex === index;
        item.setAttribute('aria-selected', String(isActive));
        item.tabIndex = isActive ? 0 : -1;
      });
      if (panel && chapter.id) panel.setAttribute('aria-labelledby', chapter.id);
      video.poster = poster;
      video.src = src;
      if (captions) {
        if (captionSrc) captions.src = captionSrc;
        else captions.removeAttribute('src');
      }
      video.setAttribute('aria-label', `Swivot demonstration: ${label}`);
      if (cue && chapterCue) cue.textContent = chapterCue;
      if (setup && chapterSetup) setup.textContent = chapterSetup;
      if (move && chapterMove) move.textContent = chapterMove;
      if (feel && chapterFeel) feel.textContent = chapterFeel;
      if (correct && chapterCorrect) correct.textContent = chapterCorrect;
      if (recovery) {
        recovery.href = src;
        recovery.hidden = true;
      }
      updateStatus(`Loading chapter ${index + 1} of ${chapters.length} · ${label}`);
      video.load();
    };

    chapters.forEach((chapter, index) => {
      chapter.addEventListener('click', () => selectChapter(chapter, index));
      chapter.addEventListener('keydown', (event) => {
        let nextIndex = null;

        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (index + 1) % chapters.length;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (index - 1 + chapters.length) % chapters.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = chapters.length - 1;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          selectChapter(chapter, index);
          return;
        }
        if (nextIndex === null) return;

        event.preventDefault();
        chapters.forEach((item, itemIndex) => {
          item.tabIndex = itemIndex === nextIndex ? 0 : -1;
        });
        chapters[nextIndex].focus();
      });
    });

    video.addEventListener('loadedmetadata', () => {
      const label = chapters[activeIndex]?.dataset.chapterLabel || 'Demonstration';
      const duration = formatDuration(video.duration);
      updateStatus(`Chapter ${activeIndex + 1} of ${chapters.length} · ${label}${duration ? ` · ${duration}` : ''}`);
    });

    video.addEventListener('error', () => {
      updateStatus('This chapter could not load. Try another chapter or open the video directly.');
      if (recovery) recovery.hidden = false;
    });

    this.root.querySelectorAll('a[href="#swivot-demo"]').forEach((link) => {
      link.addEventListener('click', () => {
        window.requestAnimationFrame(() => {
          demo.querySelector('h2')?.focus({ preventScroll: true });
        });
      });
    });
  }

  observeSections() {
    if (!('IntersectionObserver' in window) || this.motionQuery.matches) {
      this.root.classList.add('swivot-home--ready');
      return;
    }

    this.root.classList.add('swivot-home--ready');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.dataset.inView = 'true';
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.12 });

    this.root.querySelectorAll('.swivot-intro, .swivot-demo, .swivot-fundamentals, .swivot-testimonial').forEach((section) => observer.observe(section));
  }
}

const initializeSwivotHome = (scope = document) => {
  scope.querySelectorAll('[data-swivot-home]').forEach((root) => new SwivotHome(root));
};

initializeSwivotHome();

document.addEventListener('shopify:section:load', (event) => {
  initializeSwivotHome(event.target);
});
